import { Users, Target, Award, Shield } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: 'Calidad',
      description: 'Compromiso con la excelencia en cada proyecto, utilizando materiales de primera calidad y técnicas profesionales.',
    },
    {
      icon: Users,
      title: 'Confianza',
      description: 'Relaciones duraderas con nuestros clientes basadas en transparencia, honestidad y cumplimiento.',
    },
    {
      icon: Target,
      title: 'Precisión',
      description: 'Atención al detalle y exactitud en cada medida, soldadura y acabado para resultados impecables.',
    },
    {
      icon: Award,
      title: 'Experiencia',
      description: 'Años de trayectoria en el sector metalúrgico respaldando cada proyecto que realizamos.',
    },
  ];

  const team = [
    {
      name: 'Aníbal',
      role: 'Fundador y Maestro Soldador',
      image: 'https://images.unsplash.com/photo-1745448797901-2a4c9d9af1c1?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      name: 'Hugo',
      role: 'Especialista en Estructuras',
      image: 'https://images.unsplash.com/photo-1745448797900-35d08e85e9db?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
    {
      name: 'Martin',
      role: 'Técnico de Acabados',
      image: 'https://images.unsplash.com/photo-1745448797900-35d08e85e9db?crop=entropy&cs=srgb&fm=jpg&q=85',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20 md:py-32" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight uppercase mb-6">Sobre Nosotros</h1>
          <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto">
            Conoce la historia, valores y equipo detrás de MetalBrix, tu socio en soluciones metalúrgicas.
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="our-story">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-6">Nuestra Historia</h2>
              <div className="space-y-4 text-base md:text-lg leading-relaxed text-slate-600">
                <p>
                  MetalBrix nace de la pasión por el trabajo con metal y el deseo de ofrecer soluciones de alta calidad a hogares y empresas. Con años de experiencia en el sector, hemos perfeccionado nuestras técnicas y procesos para garantizar resultados excepcionales.
                </p>
                <p>
                  Comenzamos como un pequeño taller familiar y hemos crecido hasta convertirnos en un referente en soluciones metalúrgicas. Cada proyecto es único y lo abordamos con el mismo compromiso y dedicación, sin importar su tamaño.
                </p>
                <p>
                  Nuestro equipo está formado por profesionales apasionados que aman lo que hacen. Combinamos técnicas tradicionales con tecnología moderna para crear productos que no solo cumplen su función, sino que superan las expectativas de nuestros clientes.
                </p>
              </div>
            </div>

            {/* Image */}
            <div>
              <div className="aspect-[9/16] rounded-sm overflow-hidden border border-slate-200">
                <img
                  src="products/amoladoragigante.png"
                  alt="Taller MetalBrix"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission and Vision */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="mission-vision">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 border-l-4 border-blue-700">
              <h3 className="text-2xl md:text-3xl font-semibold tracking-normal text-slate-900 mb-4">Nuestra Misión</h3>
              <p className="text-base md:text-lg leading-relaxed text-slate-600">
                Proporcionar soluciones metalúrgicas de la más alta calidad que superen las expectativas de nuestros clientes, combinando artesanía tradicional con innovación moderna para crear productos duraderos y funcionales.
              </p>
            </div>
            <div className="bg-white p-8 border-l-4 border-blue-700">
              <h3 className="text-2xl md:text-3xl font-semibold tracking-normal text-slate-900 mb-4">Nuestra Visión</h3>
              <p className="text-base md:text-lg leading-relaxed text-slate-600">
                Ser reconocidos como la empresa líder en soluciones metalúrgicas, destacando por nuestra calidad, innovación y compromiso con la satisfacción del cliente, expandiendo nuestros servicios mientras mantenemos la excelencia artesanal.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-4">Nuestros Valores</h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Los principios que guían cada decisión y proyecto que realizamos.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div key={index} data-testid={`value-card-${index}`} className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-900 rounded-sm mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{value.title}</h3>
                  <p className="text-base leading-relaxed text-slate-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900 mb-4">Nuestro Equipo</h2>
            <p className="text-base md:text-lg leading-relaxed text-slate-600 max-w-2xl mx-auto">
              Profesionales dedicados con pasión por el trabajo en metal.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div key={index} data-testid={`team-member-${index}`} className="bg-white border border-slate-200 overflow-hidden">
                <div className="aspect-square overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 text-center">
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{member.name}</h3>
                  <p className="text-sm font-medium tracking-widest uppercase text-slate-500">{member.role}</p>
                </div>
              </div>
            ))}
          </div>
          
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-slate-900 text-white" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase mb-6">Trabajemos Juntos</h2>
          <p className="text-lg md:text-xl text-slate-300 mb-8">
            Estamos listos para hacer realidad tu proyecto. Contáctanos y descubre cómo podemos ayudarte.
          </p>
          <a
            href="/contacto"
            data-testid="about-contact-button"
            className="bg-white text-slate-900 hover:bg-slate-100 rounded-sm px-8 py-4 font-bold uppercase tracking-wider hover-lift hard-shadow inline-block"
          >
            Contáctanos
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;